import React from 'react'

/**
 * 아주 작은 마크다운 렌더러 (약관/개인정보/마케팅 표시용).
 * 지원: `## 제목`, `### 소제목`, `- 리스트`, `**굵게**`, 일반 문단.
 * (외부 라이브러리 없이 최소 기능만)
 */
interface MarkdownProps {
  content: string
  className?: string
}

// 인라인 **굵게** 처리
const renderInline = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

const Markdown: React.FC<MarkdownProps> = ({ content, className }) => {
  const lines = content.replace(/\r\n/g, '\n').split('\n')
  const blocks: React.ReactNode[] = []
  let listItems: string[] = []
  let keySeq = 0

  const flushList = () => {
    if (listItems.length === 0) return
    const items = listItems
    listItems = []
    blocks.push(
      <ul
        key={`ul-${keySeq++}`}
        className="list-disc list-inside font-pretendard font-regular text-16px text-textColorText2 leading-relaxed space-y-4px ml-4px"
      >
        {items.map((it, i) => (
          <li key={i}>{renderInline(it)}</li>
        ))}
      </ul>
    )
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd()
    if (line.trim() === '') {
      flushList()
      continue
    }
    if (line.startsWith('- ')) {
      listItems.push(line.slice(2))
      continue
    }
    flushList()
    if (line.startsWith('### ')) {
      blocks.push(
        <h3
          key={`h3-${keySeq++}`}
          className="font-pretendard font-bold text-16px text-textColorText2 mt-8px"
        >
          {renderInline(line.slice(4))}
        </h3>
      )
    } else if (line.startsWith('## ')) {
      blocks.push(
        <h2
          key={`h2-${keySeq++}`}
          className="font-pretendard font-bold text-18px text-textColorText2 mt-8px"
        >
          {renderInline(line.slice(3))}
        </h2>
      )
    } else if (line.startsWith('# ')) {
      blocks.push(
        <h1
          key={`h1-${keySeq++}`}
          className="font-pretendard font-bold text-22px text-textColorText2 mt-8px"
        >
          {renderInline(line.slice(2))}
        </h1>
      )
    } else {
      blocks.push(
        <p
          key={`p-${keySeq++}`}
          className="font-pretendard font-regular text-16px text-textColorText2 leading-relaxed whitespace-pre-wrap"
        >
          {renderInline(line)}
        </p>
      )
    }
  }
  flushList()

  return (
    <div className={className ?? 'flex flex-col gap-12px'}>{blocks}</div>
  )
}

export default Markdown
